import React from 'react';
import { motion } from 'framer-motion';
import { 
  Building2, 
  Shield, 
  Lock, 
  AlertTriangle, 
  Database,
  CheckCircle,
  ArrowRight,
  FileCheck,
  Network,
  Users
} from 'lucide-react';

interface FinancialServicesProps {
  setCurrentPage: (page: string) => void;
}

const FinancialServices: React.FC<FinancialServicesProps> = ({ setCurrentPage }) => {
  const features = [
    {
      icon: Shield,
      title: "Advanced Security",
      description: "Comprehensive security solutions for financial institutions.",
      items: [
        "Multi-layer security architecture",
        "Real-time threat detection",
        "Fraud prevention systems",
        "Transaction monitoring"
      ]
    },
    {
      icon: Lock,
      title: "Compliance & Regulation",
      description: "Ensure compliance with financial industry regulations.",
      items: [
        "PCI DSS compliance",
        "GDPR requirements",
        "SOX compliance",
        "Regulatory reporting"
      ]
    },
    {
      icon: Database,
      title: "Data Protection",
      description: "Secure sensitive financial and customer data.",
      items: [
        "Encryption at rest and in transit",
        "Access control management",
        "Data loss prevention",
        "Secure data storage"
      ]
    },
    {
      icon: AlertTriangle,
      title: "Risk Management",
      description: "Comprehensive risk assessment and mitigation.",
      items: [
        "Risk assessment framework",
        "Vulnerability management",
        "Incident response planning",
        "Business continuity"
      ]
    }
  ];

  const solutions = [
    {
      icon: FileCheck,
      title: "Regulatory Compliance",
      items: [
        "Compliance monitoring",
        "Audit preparation",
        "Policy enforcement",
        "Regulatory reporting"
      ]
    },
    {
      icon: Network,
      title: "Infrastructure Security",
      items: [
        "Network protection",
        "Cloud security",
        "API security",
        "Access control"
      ]
    },
    {
      icon: Users,
      title: "Customer Protection",
      items: [
        "Identity verification",
        "Fraud prevention",
        "Transaction security",
        "Data privacy"
      ]
    }
  ];

  return (
    <div className="min-h-screen hero-gradient pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block mb-6"
          >
            <Building2 className="h-16 w-16 text-primary animate-glow" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            <span className="text-gradient">Financial Services Security</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-muted max-w-3xl mx-auto"
          >
            Advanced cybersecurity solutions designed specifically for banks, insurance companies, and financial institutions
          </motion.p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              className="glass-panel rounded-xl p-8 hover-card"
            >
              <div className="flex items-center mb-6">
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                  <feature.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-semibold ml-4">{feature.title}</h3>
              </div>
              
              <p className="text-muted mb-6">{feature.description}</p>
              
              <ul className="space-y-3">
                {feature.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                    <span className="text-muted">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Solutions Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Comprehensive Solutions</h2>
            <p className="text-lg text-muted">
              Tailored security solutions for the financial sector
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {solutions.map((solution, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                    <solution.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold ml-4">{solution.title}</h3>
                </div>
                
                <ul className="space-y-3">
                  {solution.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                      <span className="text-muted">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="text-center"
        >
          <div className="glass-panel rounded-2xl p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-6">Secure Your Financial Institution</h2>
            <p className="text-lg text-muted mb-8 max-w-2xl mx-auto">
              Contact our financial security experts to discuss your institution's specific needs and discover how we can help protect your assets
            </p>
            <button 
              onClick={() => setCurrentPage('get-started')}
              className="btn-primary"
            >
              <span className="flex items-center justify-center">
                Request Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </span>
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default FinancialServices;